/* eslint-disable no-restricted-syntax */
/* eslint-disable no-plusplus */

// IBAN country code → expected total length
const IBAN_LENGTHS: Record<string, number> = {
  AD: 24,
  AE: 23,
  AL: 28,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BR: 29,
  BY: 28,
  CH: 21,
  CR: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  DO: 28,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GE: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IQ: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MR: 27,
  MT: 31,
  MU: 30,
  NL: 18,
  NO: 15,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  SA: 24,
  SC: 31,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  ST: 25,
  SV: 28,
  TL: 23,
  TN: 24,
  TR: 26,
  UA: 29,
  VA: 22,
  VG: 24,
  XK: 20,
};

/**
 * Validates the Spanish domestic check digits (positions 11-12).
 * These two digits verify the bank code, branch code, and account number.
 */
const validateSpanishCheckDigits = (iban: string): boolean => {
  const bankCode = iban.substring(4, 8);
  const branchCode = iban.substring(8, 12);
  const checkDigits = iban.substring(12, 14);
  const accountNumber = iban.substring(14, 24);

  const computeCheckDigit = (input: string): number => {
    const weights = [1, 2, 4, 8, 5, 10, 9, 7, 3, 6];
    let sum = 0;
    for (let i = 0; i < input.length; i++) {
      sum += parseInt(input[i], 10) * weights[i];
    }
    const remainder = 11 - (sum % 11);
    if (remainder === 11) return 0;
    if (remainder === 10) return 1;
    return remainder;
  };

  const firstDigit = computeCheckDigit(`00${bankCode}${branchCode}`);
  const secondDigit = computeCheckDigit(accountNumber);

  return checkDigits === `${firstDigit}${secondDigit}`;
};

/**
 * Performs the universal mod-97 IBAN check (ISO 13616).
 * Moves the first 4 characters to the end, converts letters to numbers,
 * and checks that the remainder when divided by 97 equals 1.
 */
const mod97Check = (iban: string): boolean => {
  const rearranged = iban.substring(4) + iban.substring(0, 4);

  const numericString = rearranged
    .split('')
    .map((char) => {
      const code = char.charCodeAt(0);
      return code >= 65 && code <= 90 ? (code - 55).toString() : char;
    })
    .join('');

  // Process in chunks to avoid BigInt issues
  let remainder = '';
  for (const digit of numericString) {
    remainder += digit;
    remainder = (parseInt(remainder, 10) % 97).toString();
  }

  return parseInt(remainder, 10) === 1;
};

export type IBANValidationResult = {
  isValid: boolean;
  error?: string;
};

const validateIBAN = (value: string): IBANValidationResult => {
  const iban = value.replace(/\s+/g, '').toUpperCase();

  if (!iban) {
    return { isValid: false, error: 'IBANRequired' };
  }

  if (!/^[A-Z]{2}[0-9A-Z]+$/.test(iban)) {
    return { isValid: false, error: 'IBANInvalidFormat' };
  }

  const countryCode = iban.substring(0, 2);
  const expectedLength = IBAN_LENGTHS[countryCode];

  if (!expectedLength) {
    return { isValid: false, error: 'IBANUnsupportedCountry' };
  }

  if (iban.length !== expectedLength) {
    return {
      isValid: false,
      error: 'IBANInvalidLength',
    };
  }

  if (!mod97Check(iban)) {
    return { isValid: false, error: 'IBANInvalidCheckDigits' };
  }

  // Spain-specific: validate domestic check digits
  if (countryCode === 'ES' && !validateSpanishCheckDigits(iban)) {
    return { isValid: false, error: 'IBANInvalidSpanishCheckDigits' };
  }

  return { isValid: true };
};

export default validateIBAN;
